import React, { Fragment, memo, ReactNode, useCallback, useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';

interface Props {
  labels: string[];
  onChange?: (index: number) => void;
  children?: ReactNode[];
  variant?: 'default' | 'secondary';
  controls?: ReactNode[];
}

function CustomTabs(props: Props) {
  const { labels, onChange, variant = 'default', controls, children } = props;
  const [tabIndex, setTabIndex] = useState<number>(0);

  const onValueChange = useCallback(
    (value: string) => {
      const idx = Number(value);
      setTabIndex(idx);
      if (onChange) onChange(idx);
    },
    [onChange]
  );

  if (!labels) return null;

  return (
    <Tabs
      value={String(tabIndex)}
      onValueChange={onValueChange}
      className="w-full"
    >
      <div className="flex flex-col sm:flex-row sm:items-center bg-white gap-2">
        <TabsList
          className={
            variant === 'secondary'
              ? 'bg-muted p-1 rounded-lg flex flex-col sm:flex-row w-full sm:w-auto h-auto'
              : 'bg-gray-50 p-1 rounded-lg flex flex-col sm:flex-row w-full sm:w-auto h-auto gap-1'
          }
        >
          {labels.map((label, idx) => (
            <TabsTrigger
              key={idx}
              value={String(idx)}
              className={
                variant === 'secondary'
                  ? 'h-10 font-medium rounded-md px-4 data-[state=active]:bg-white data-[state=active]:shadow w-full sm:w-auto justify-center'
                  : 'h-10 sm:h-12 font-medium px-4 rounded-md data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm hover:bg-gray-100 transition-colors w-full sm:w-auto justify-center'
              }
            >
              {label}
            </TabsTrigger>
          ))}
        </TabsList>
        {!!controls && (
          <div className="flex items-center sm:ml-auto gap-4 px-2">
            {controls.map((control, i) => (
              <Fragment key={i}>{control}</Fragment>
            ))}
          </div>
        )}
      </div>
      {children &&
        children.map((tabContent, idx) => (
          <TabsContent key={idx} value={String(idx)} className="p-4 sm:p-6">
            {tabContent}
          </TabsContent>
        ))}
    </Tabs>
  );
}

export default memo(CustomTabs);
